import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, CalendarDays, Plus, CheckCircle2, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import {
  Card, Button, Field, Input, Select, Textarea, Pill, Spinner, EmptyState,
} from "@/components/ui";

// =============================================================================
// MyLeaveScreen — /leave
// Employee self-serve leave portal. Balance widgets at top, history list
// below, "Request leave" button spawns a modal form that calls the
// submit_leave_request RPC. T-007 Commit 1.
// =============================================================================

// Migration 328 — the balance is COMPUTED, not stored. v_leave_position =
// director-entered opening (as at its date, from Xero) + award accrual from
// submitted timesheet hours since then − approved leave since then. The same
// view feeds this screen, Leave approvals and the employee file, so all three
// always show the same number.
interface LeavePosition {
  employee_id: string;
  accrues:     boolean;
  has_opening: boolean;
  as_at:       string;
  al_opening: number; al_accrued: number; al_taken: number; al_days: number;
  sl_opening: number; sl_accrued: number; sl_taken: number; sl_days: number;
  lsl_weeks:  number;
  rdo_days:   number;
}

interface LeaveTypeRow {
  leave_type_code:    string;
  display_name:       string;
  is_paid:            boolean;
  requires_approval:  boolean;
  sort_order:         number;
}

interface RequestRow {
  request_id:     string;
  request_number: string;
  leave_type_code: string;
  starts_on:      string;
  ends_on:        string;
  days_requested: number;
  reason:         string | null;
  status:         string;
  submitted_at:   string | null;
  approved_at:    string | null;
  rejection_notes: string | null;
  leave_type?: { display_name: string } | null;
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    return `${dt.getDate().toString().padStart(2, "0")}/${(dt.getMonth() + 1).toString().padStart(2, "0")}/${dt.getFullYear()}`;
  } catch { return d; }
}

function statusTone(s: string): "ok" | "warning" | "critical" | "default" | "pending" {
  switch (s) {
    case "approved":  return "ok";
    case "submitted": return "pending";
    case "rejected":  return "critical";
    case "cancelled": return "default";
    case "taken":     return "ok";
    default:          return "default";
  }
}

// -----------------------------------------------------------------------------

export default function MyLeaveScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: position } = useQuery({
    queryKey: ["my_leave_position", user?.employee_id],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      // v_leave_position is not in the generated types until the next regen,
      // hence the cast.
      const { data, error } = await supabase
        .from("v_leave_position" as any)
        .select("*")
        .eq("employee_id", user!.employee_id!)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as LeavePosition | null;
    },
  });

  const { data: requests = [], isLoading: reqLoading } = useQuery({
    queryKey: ["my_leave_requests", user?.employee_id],
    enabled: !!user?.employee_id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leave_requests")
        .select(`
          request_id, request_number, leave_type_code, starts_on, ends_on,
          days_requested, reason, status, submitted_at, approved_at, rejection_notes,
          leave_type:leave_types(display_name)
        `)
        .eq("employee_id", user!.employee_id!)
        .order("starts_on", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as RequestRow[];
    },
  });

  if (!user) return null;

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto pb-12">
      <button
        onClick={() => navigate("/portal")}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> Portal
      </button>

      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-gradient-to-br from-accent/20 to-accent/5 text-accent flex items-center justify-center border border-accent/30">
            <CalendarDays size={22} />
          </div>
          <div>
            <h1 className="text-[24px] font-semibold text-text">My leave</h1>
            <p className="text-[13px] text-text-muted mt-0.5">Submit requests, track balance, review history.</p>
          </div>
        </div>
        <Button icon={Plus} onClick={() => setModalOpen(true)}>Request leave</Button>
      </div>

      {/* Balances — live position, computed in the DB. James, 26 Aug 2026:
          "leave should be based on the employee start date ... timesheets are
          too unpredictable while we are so small." Migration 330: accrual is
          by the CALENDAR — 20 annual / 10 personal days per year of service,
          pro-rata to the day, from the start date. A director-entered opening
          balance remains as the override; accrual then counts from its date. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
        <BalanceCard label="Annual" value={position?.al_days ?? 0} unit="days"
          breakdown={position ? `${fmt1(position.al_opening)} + ${fmt1(position.al_accrued)} accrued − ${fmt1(position.al_taken)} taken` : undefined} />
        <BalanceCard label="Personal / sick" value={position?.sl_days ?? 0} unit="days"
          breakdown={position ? `${fmt1(position.sl_opening)} + ${fmt1(position.sl_accrued)} accrued − ${fmt1(position.sl_taken)} taken` : undefined} />
        <BalanceCard label="LSL" value={position?.lsl_weeks ?? 0} unit="weeks" quiet />
        <BalanceCard label="RDO" value={position?.rdo_days ?? 0} unit="days" quiet />
      </div>
      {position && (
        <div className="mb-6 text-[11px] text-text-muted font-mono">
          {position.has_opening
            ? <>Opening balances as at {fmtDate(position.as_at)} · accruing since at 20 annual / 10 personal days a year.</>
            : <>Accruing from your start date ({fmtDate(position.as_at)}) at 20 annual / 10 personal days a year.</>}
        </div>
      )}
      {!position && <div className="mb-4" />}

      {/* History */}
      <div className="mb-3 flex items-center gap-3">
        <div className="h-1 w-10 bg-accent rounded-full" />
        <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
          My requests
        </div>
      </div>
      <Card>
        {reqLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : requests.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title="No leave requests yet"
            description="Click Request leave above to submit your first."
          />
        ) : (
          <div className="divide-y divide-line">
            {requests.map(r => <RequestRowView key={r.request_id} r={r} />)}
          </div>
        )}
      </Card>

      {modalOpen && (
        <NewLeaveModal
          onClose={() => setModalOpen(false)}
          onCreated={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function fmt1(n: number | null | undefined): string {
  return Number(n ?? 0).toFixed(1);
}

function BalanceCard({ label, value, unit, breakdown, quiet }: {
  label: string; value: number; unit: string; breakdown?: string; quiet?: boolean;
}) {
  // quiet = LSL and RDO: sitting at zero is normal for a young company, so no
  // red. For annual/personal, red genuinely means out of leave and amber means
  // running low - real signals now accrual runs by the calendar.
  const tone =
    quiet       ? "text-text" :
    value <= 0  ? "text-danger" :
    value <= 3  ? "text-warn"   :
    "text-text";
  return (
    <Card className="relative overflow-hidden">
      <div className="absolute top-0 left-0 h-0.5 w-full bg-accent/40" />
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono">{label}</div>
        <div className={`mt-1 text-[22px] font-semibold font-mono ${tone}`}>
          {Number(value).toFixed(1)}
        </div>
        <div className="text-[11px] text-text-muted">{unit} remaining</div>
        {breakdown && (
          <div className="mt-1 text-[10px] text-text-muted/80 font-mono truncate" title={breakdown}>
            {breakdown}
          </div>
        )}
      </div>
    </Card>
  );
}

function RequestRowView({ r }: { r: RequestRow }) {
  const qc = useQueryClient();
  const cancelMut = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.schema("app").rpc("cancel_leave_request", { p_request_id: r.request_id });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my_leave_requests"] });
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : "Cancel failed"),
  });

  const typeName = r.leave_type?.display_name ?? r.leave_type_code;
  const tone = statusTone(r.status);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[12px] text-accent">{r.request_number}</span>
          <span className="text-[13px] text-text">{typeName}</span>
          <Pill
            label={r.status}
            tone={tone === "pending" ? "pending" : tone === "ok" ? "ok" : tone === "critical" ? "critical" : "default"}
            small
          />
        </div>
        {r.status === "submitted" && (
          <Button variant="ghost" size="sm" onClick={() => cancelMut.mutate()} disabled={cancelMut.isPending}>
            {cancelMut.isPending ? "Cancelling…" : "Cancel"}
          </Button>
        )}
      </div>
      <div className="text-[12px] text-text-muted mt-1.5">
        {fmtDate(r.starts_on)} → {fmtDate(r.ends_on)} · {Number(r.days_requested).toFixed(1)} day{Number(r.days_requested) === 1 ? "" : "s"}
      </div>
      {r.reason && (
        <div className="text-[12px] text-text mt-1">{r.reason}</div>
      )}
      {r.status === "rejected" && r.rejection_notes && (
        <div className="mt-1.5 p-2 rounded bg-danger/10 border border-danger/30 text-[12px] text-danger">
          Declined: {r.rejection_notes}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function NewLeaveModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const qc = useQueryClient();
  // "AL", not "annual" — the seeded leave_types codes are AL/SL/LSL/RDO/…
  // The old default was a code that does not exist, so the pre-selected option
  // rendered blank and a submit without touching the dropdown was rejected.
  const [leave_type_code, setLeaveType] = useState("AL");
  const [starts_on, setStart] = useState("");
  const [ends_on, setEnd]     = useState("");
  const [reason, setReason]   = useState("");
  const [err, setErr]         = useState<string | null>(null);

  const { data: leaveTypes = [] } = useQuery({
    queryKey: ["leave_types_active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leave_types")
        .select("leave_type_code, display_name, is_paid, requires_approval, sort_order")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as LeaveTypeRow[];
    },
  });

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!starts_on || !ends_on) throw new Error("Start and end dates required.");
      const { error } = await supabase.schema("app").rpc("submit_leave_request", {
        p_leave_type_code: leave_type_code,
        p_starts_on: starts_on,
        p_ends_on: ends_on,
        p_reason: reason.trim() || undefined,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my_leave_requests"] });
      qc.invalidateQueries({ queryKey: ["my_leave_position"] });
      onCreated();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Submit failed"),
  });

  const approxDays = useMemo(() => {
    if (!starts_on || !ends_on) return null;
    const s = new Date(starts_on), e = new Date(ends_on);
    if (e < s) return null;
    // quick approx — excludes weekends only, ignores public holidays (server computes the real value)
    let count = 0;
    const cur = new Date(s);
    while (cur <= e) {
      const d = cur.getDay();
      if (d !== 0 && d !== 6) count++;
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  }, [starts_on, ends_on]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <Card>
          <div className="p-5 border-b border-line flex items-center justify-between">
            <div>
              <div className="text-[14px] font-medium text-text">New leave request</div>
              <div className="text-[12px] text-text-muted">A director/manager will review before it's approved.</div>
            </div>
            <button onClick={onClose} className="text-text-muted hover:text-text" type="button">
              <X size={18} />
            </button>
          </div>
          <div className="p-5 space-y-3">
            <Field label="Leave type *">
              <Select value={leave_type_code} onChange={e => setLeaveType(e.target.value)}>
                {leaveTypes.map(t => (
                  <option key={t.leave_type_code} value={t.leave_type_code}>
                    {t.display_name}{!t.is_paid ? " (unpaid)" : ""}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start date *">
                <Input type="date" mono value={starts_on} onChange={e => setStart(e.target.value)} />
              </Field>
              <Field label="End date *">
                <Input type="date" mono value={ends_on} onChange={e => setEnd(e.target.value)} />
              </Field>
            </div>
            {approxDays != null && (
              <div className="text-[11px] text-text-muted font-mono">
                ~{approxDays} working day{approxDays === 1 ? "" : "s"} (excludes weekends; public holidays checked server-side).
              </div>
            )}
            <Field label="Reason (optional)" hint="Helpful context for whoever approves.">
              <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Family trip; medical appointment." />
            </Field>
            {err && (
              <div className="text-[12px] text-red-400 bg-red-900/20 border border-red-900/40 rounded px-3 py-2">
                {err}
              </div>
            )}
          </div>
          <div className="p-4 border-t border-line flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button
              icon={CheckCircle2}
              onClick={() => submitMut.mutate()}
              disabled={!starts_on || !ends_on || submitMut.isPending}
              type="button"
            >
              {submitMut.isPending ? "Submitting…" : "Submit request"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

